import React from 'react';
import {
  AppBar, AppBarSection, AppBarSpacer, Menu, MenuItem,
} from '@progress/kendo-react-layout';
import { useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { FpFormattedMessage } from 'locales/FpFormattedMessage';
import { FpIconButton } from 'components/molecules/button/FpIconButton';
import { FpUserButton } from 'components/molecules/button/FpUserButton';
import { FpHeaderMenu } from 'components/molecules/FpHeaderMenu';
import { FpHeaderProjectStatus } from 'components/molecules/FpHeaderProjectStatus';
import { useMedia } from 'react-use';
import { LoginUser } from 'types/model/user';
import { ServiceProjectPolicyResponse, ServiceProjectPolicyResponseServiceProjectPolicyEnum } from 'types/entity';
import config from 'environment';
import styles from './style.module.scss';

export type FpHeaderProps = {
  userName: string
  companyName: string
  displayLocale: string
  zoneinfo: LoginUser['zoneinfo']
  projectName: string
  projectTypeName: string
  projectStatus: string
  projectId: string
  isProjectAdmin: boolean
  isFpReadableProject: boolean
  isFpEditableProject: boolean
  isEditableExtendedAttributeProject: boolean
  isDdmsReadableProject: boolean
  isPhotoReadableProject: boolean
  serviceProjectPolicies: ServiceProjectPolicyResponse[]
  isProjectInvitationServiceAuth: boolean
  isGroupManagerServiceAuth: boolean
}

export function FpHeader(props: FpHeaderProps) {
  const navigate = useNavigate();
  const isTabletSize = useMedia('(max-width: 960px)');

  const menuRender = () => (
    <>
      <FpUserButton />
      <div className={styles.header__section__userName}>{props.userName}</div>
    </>
  );

  const logoAttrs = (): {[key: string]: any } => {
    if (props.projectId) {
      return { href: '/' };
    }
    return { href: '#', disabled: true, style: { pointerEvents: 'none' } };
  };

  const handleUserMenu = (e:any) => {
    if (!e.item.data) {
      return;
    }
    switch (e.item.data.action) {
      case 'logout': {
        navigate('/logout');
        break;
      }
    }
  };

  const handleServiceButton = (url: string | undefined) => {
    if (!url) {
      navigate('/error?type=/error/global/0000&status=500');
      return;
    }
    window.open(url, '_blank', 'noopener');
  };

  const handleServiceMenu = (e: any) => {
    if (!e.item.data) {
      return;
    }
    if (!e.item.data.url) {
      navigate('/error?type=/error/global/0000&status=500');
      return;
    }
    window.open(e.item.data.url, '_blank', 'noopener');
  };

  const handleSerachButton = () => {
    navigate('/search');
  };

  const descriptionRender = (label: string) => (
    <div className={styles.header__description}>{label}</div>
  );

  return (
    <AppBar positionMode="sticky" style={{ flexWrap: 'nowrap', zIndex: 102 }}>
      <AppBarSection>
        <a
          {...logoAttrs()}
          className="k-button k-button-clear k-p-0"
          style={{ textTransform: 'inherit' }}
        >
          <img src={config.filePortalLogo} className={styles.header__logo} alt="logo" />
          <div className={styles['header__service-name']}>
            <FpFormattedMessage id="service.title" />
          </div>
        </a>
      </AppBarSection>

      { !!props.projectName && (
      <FpHeaderMenu
        projectId={props.projectId}
        projectName={props.projectName}
        projectTypeName={props.projectTypeName}
        projectStatus={props.projectStatus}
        isProjectAdmin={props.isProjectAdmin}
        isFpReadableProject={props.isFpReadableProject}
        isFpEditableProject={props.isFpEditableProject}
        isEditableExtendedAttributeProject={props.isEditableExtendedAttributeProject}
        anySubsystemServiceOpened={props.isDdmsReadableProject || props.isPhotoReadableProject}
        isProjectInvitationServiceAuth={props.isProjectInvitationServiceAuth}
        isGroupManagerServiceAuth={props.isGroupManagerServiceAuth}
      />
      ) }

      <div className={styles.header__section__chip}>
        <FpHeaderProjectStatus projectTypeName={props.projectTypeName} projectStatus={props.projectStatus} />
      </div>

      <AppBarSpacer />

      { !!props.projectName && (
        <AppBarSection>
          <FpIconButton onClick={handleSerachButton} icon={<span className="k-icon k-i-search" />} />
        </AppBarSection>
      ) }

      <div className={styles.header__section__pc}>
        <AppBarSection>
          {
            props.serviceProjectPolicies
              .filter((policy) => policy.service_project_policy !== ServiceProjectPolicyResponseServiceProjectPolicyEnum.Hidden)
              .map((policy, index, policies) => (
                <Button
                  disabled={policy.service_project_policy === ServiceProjectPolicyResponseServiceProjectPolicyEnum.Disabled}
                  className={index !== policies.length - 1 ? 'k-mr-2' : ''}
                  style={{ textTransform: 'inherit' }}
                  onClick={() => handleServiceButton(policy.url)}
                >
                  {policy.service_menu_button_display_name}
                </Button>
              ))
          }
        </AppBarSection>
      </div>

      {
        (props.serviceProjectPolicies.length > 0) && (
          <div className={styles.header__section__sp}>
            <AppBarSection>
              <Menu onSelect={handleServiceMenu}>
                <MenuItem cssClass={styles.header__menu} render={() => <FpIconButton icon={<span className="k-icon k-i-grid" />} />}>
                  {
                    props.serviceProjectPolicies
                      .filter((policy) => policy.service_project_policy !== ServiceProjectPolicyResponseServiceProjectPolicyEnum.Hidden)
                      .map((policy) => (
                        <MenuItem
                          disabled={policy.service_project_policy === ServiceProjectPolicyResponseServiceProjectPolicyEnum.Disabled}
                          cssClass={styles.header__menuItem}
                          text={policy.service_menu_button_display_name}
                          data={{ url: policy.url }}
                        />
                      ))
                  }
                </MenuItem>
              </Menu>
            </AppBarSection>
          </div>
        )
      }

      <AppBarSection>
        <span className="k-appbar-separator" />
      </AppBarSection>

      <div className={styles.header__section__user}>
        {
          !!props.userName
          && (
          <AppBarSection>
            <Menu onSelect={handleUserMenu}>
              <MenuItem cssClass={styles.header__menu} render={menuRender}>
                {
                  isTabletSize
                && <MenuItem linkRender={() => descriptionRender(props.userName)} cssClass={styles.header__menuItem} />
                }
                <MenuItem linkRender={() => descriptionRender(props.companyName)} cssClass={styles.header__menuItem} />
                <MenuItem linkRender={() => descriptionRender(props.displayLocale)} cssClass={styles.header__menuItem} />
                <MenuItem linkRender={() => descriptionRender(props.zoneinfo)} cssClass={styles.header__menuItem} />
                <MenuItem render={() => <FpFormattedMessage id="logout" />} data={{ action: 'logout' }} />
              </MenuItem>
            </Menu>
          </AppBarSection>
          )
        }
      </div>

    </AppBar>
  );
}
